import { filter, orderBy, forEach, sortBy } from 'lodash-es';
import moment from 'moment';

/**
 * Retrieves a list of filtered bookings from the
 * currently selected filters in store.
 *
 * @param state
 * @returns {Array|*}
 */
export const filteredBookings = (state) => {
    const _selected = state.bookings.filters.selected;
    let _bookings = state.bookings.value;
    let _predicate = state.bookings.predicate;
    let _order = state.bookings.order;
    let _searchword = state.bookings.filters.searchword;

    // Filter by searchword
    if (_searchword !== '') {
        _bookings = filter(_bookings, (booking) => {
            return (booking.supplierRef) ?
                booking.supplierRef.toString().toLowerCase().indexOf(_searchword.toLowerCase()) !== -1
                || booking.bookingRef.toString().toLowerCase().indexOf(_searchword.toLowerCase()) !== -1
                || booking.customerName.toLowerCase().indexOf(_searchword.toLowerCase()) !== -1
                : false;
        })
    }

    if (typeof _selected.brand !== 'undefined') {
        _bookings = filter(_bookings, ['brand', _selected.brand]);
    }

    if (typeof _selected.domain !== 'undefined') {
        _bookings = filter(_bookings, ['domain', _selected.domain]);
    }

    if (typeof _selected.site !== 'undefined') {
        _bookings = filter(_bookings, ['siteName', _selected.site]);
    }

    if (typeof _selected.accommodation !== 'undefined') {
        _bookings = filter(_bookings, ['accommodation', _selected.accommodation]);
    }

    if (typeof _selected.country !== 'undefined') {
        _bookings = filter(_bookings, ['bookingCountry', _selected.country]);
    }

    if (typeof _selected.status !== 'undefined') {
        _bookings = filter(_bookings, ['status', _selected.status]);
    }

    return orderBy(_bookings, _predicate, [_order]);
};

/**
 * Retrieves a paginated list of the filtered bookings
 *
 * @param state
 * @param getters
 * @returns {string}
 */
export const filteredBookingsPaginated = (state, getters) => {
    let _page = state.bookings.page;
    let _limit = state.bookings.limit;

    return getters.filteredBookings.slice(_page * _limit - _limit, _page * _limit);
};

/**
 * Retrieves the maximum number of pages returned
 * from the paginated list of filtered bookings
 *
 * @param state
 * @param getters
 * @returns {number}
 */
export const filteredBookingsMaxPages = (state, getters) => {
    let _limit = state.bookings.limit;
    return Math.ceil(getters.filteredBookings.length / _limit);
};

/**
 * Retrieves a list of site names from supplier filter data
 *
 * @param state
 * @returns {string[]}
 */
export const siteNames = (state) => {
    return Object.keys(state.bookings.filters.sites);
};

/**
 * Generate a csv object from the filtered bookings list.
 *
 * @param state
 * @param getters
 * @returns {string}
 */
export const filteredBookingsCSVFile = (state, getters) => {
    const filteredBookings = getters.filteredBookings;
    // Add the headings to the csv file
    let csvString = 'data:text/csv;charset=utf-8,Camping Vision Ref., Booking Ref., Booking date, Arrival date, Departure date, Duration, Accommodation, Site, Brand, Domain, Customer, Booking country, Booking status, Supplier\n';
    // Iterate through the filtered bookings to create the csv object
    forEach(filteredBookings, (booking) => {
        forEach(booking, (value, key) => {
            // If date then convert to format DD/MM/YYYY
            if (key === 'bookingDate' || key === 'arrivalDate' || key === 'departureDate') {
                value = moment(value).format('DD/MM/YYYY');
            }
            // Uppercase first letter in the status
            if (key === 'status') {
                value = value.charAt(0).toUpperCase() + value.slice(1);
            }
            // Add value to the row
            csvString += value + ',';
        });
        // Remove the last comma and add line break
        csvString = csvString.substring(0, csvString.length - 1);
        csvString += '\n';
    });
    return csvString;
};

/**
 * Return filtered booking list as XLS file
 * @param state
 * @param getters
 * @returns {string}
 */
export const filteredBookingsXLSFile = (state, getters) => {
    const filteredBookings = getters.filteredBookings;
    // Add the headings to the csv file
    let xlsString = 'data:application/vnd.ms-excel,';
    // Set the headers
    xlsString += "<table border='2px'>" +
        "<thead>" +
        "<th>Camping Vision Ref.</th>" +
        "<th>Booking Ref.</th>" +
        "<th>Booking date</th>" +
        "<th>Arrival date</th>" +
        "<th>Departure date</th>" +
        "<th>Duration</th>" +
        "<th>Accommodation</th>" +
        "<th>Site</th>" +
        "<th>Brand</th>" +
        "<th>Domain</th>" +
        "<th>Customer</th>" +
        "<th>Booking country</th>" +
        "<th>Booking status</th>" +
        "<th>Supplier</th>" +
        "</thead>" +
        "<tbody>";
    // Iterate through the filtered bookings to create the csv object
    forEach(filteredBookings, (booking) => {
        // Add opening row tag
        xlsString += "<tr>";
        forEach(booking, (value, key) => {
            // Add opening table data tag
            xlsString += "<td>";
            // If date then convert to format DD/MM/YYYY
            if (key === 'bookingDate' || key === 'arrivalDate' || key === 'departureDate') {
                value = moment(value).format('DD/MM/YYYY');
            }
            // Uppercase first letter in the status
            if (key === 'status') {
                value = value.charAt(0).toUpperCase() + value.slice(1);
            }
            // Add value to the row and close table data tag
            xlsString += value + '</td>';
        });
        // Add closing row tag
        xlsString += '</tr>';
    });
    // Close the body tag
    xlsString += '</tbody>';
    return xlsString;
};

/**
 * Return a list of all the domains available sorted.
 *
 * @param state
 * @returns {Array}
 */
export const allDomains = (state) => {
    let domains = [];
    forEach(state.bookings.filters.domains, (domain) => {
        forEach(domain, (brand) => {
            domains.push(brand);
        })
    });
    return sortBy(domains, [(o) =>{return o}]);
};

/**
 * Returns a list of all the accommodations available sorted.
 *
 * @param state
 * @returns {Array}
 */
export const allAccommodations = (state) => {
    let accommodations = [];
    forEach(state.bookings.filters.sites, (site) => {
        forEach(site, (accommodation) => {
            if (accommodations.indexOf(accommodation) === -1) {
                accommodations.push(accommodation);
            }
        })
    });
    return sortBy(accommodations, [(o) =>{return o}]);
};



// WEBPACK FOOTER //
// ./src/store/modules/booking/getters.js